import Vue from 'vue';
import { CampaignService, UtilsService } from '@/services';
import { toDate, isValueExist, generateId, isValidAppStoreUrl } from '@util';
import {
    adjustDuplicate,
    filterNewAds,
    isFbFamily,
    isGoogleType,
    baseType as _baseType,
    isYelpSearch,
    isAppleSearch,
    isYouTube,
} from '@/utils/adsUtils';
import moment from 'moment';
import { notEq } from '@util';

import { CONNECTION_TYPES, OBJECTIVES } from '@consts';
import { checkRemarketingAdvSet } from '@/components/Proposal/TargetType';

const { FB_APP, PIXEL, GA } = CONNECTION_TYPES;

var getDataSchedule = R.path(['data', 'schedule']);
var getBackupSchedule = R.compose(
    (data) => {
        if (data.endDate) {
            data.endDate = toDate(data.endDate);
        }
        if (data.startDate) {
            data.startDate = toDate(data.startDate);
        }
        return data;
    },
    R.clone,
    R.propOr({}, 'schedule'),
    R.propOr({}, '_backupData')
);

var getDataBudget = R.pathOr({}, ['data', 'budget']);
var getBackupBudget = R.compose(
    R.clone,
    R.propOr({}, 'budget'),
    R.propOr({}, ['_backupData'])
);

var getChannelAds = R.pathOr([], ['data', 'channelAds']);
var getBackupChannelAds = R.pathOr([], ['_backupData', 'channelAds']);
var getDataChannels = R.pathOr([], ['data', 'channels']);
var getDataSites = R.pathOr([], ['data', 'sites']);
var getDataSearchEngines = R.pathOr([], ['data', 'searchEngines']);

var getDataTargets = R.pathOr([], ['data', 'objectives', 'targets']);

var getDataUrls = R.compose(R.filter(R.propEq('type', 'link')), getDataTargets);
var getDataContacts = R.compose(
    R.filter(R.propEq('type', 'call')),
    getDataTargets
);

var adjustProfile = (arr = []) => {
    var newArr = arr.slice(0);
    var size;
    var decisionMaker;

    var idx = R.findIndex(R.propEq('name', 'companySizeDecisionMaker'), newArr);
    if (idx > -1) {
        size = newArr.splice(idx, 1)[0];
    }

    idx = R.findIndex(R.propEq('name', 'decisionMaker'), newArr);
    if (idx > -1) {
        decisionMaker = newArr.splice(idx, 1)[0];
    }

    const idNewAndReturning = R.findIndex(
        R.propEq('name', 'newAndReturningCustomers'),
        newArr
    );
    if (idNewAndReturning > -1) {
        arr.splice(idNewAndReturning, 1)[0];
    }

    if (size && decisionMaker) {
        newArr.splice(idx, 0, {
            name: 'sizeAndDecision',
            values: {
                sizes: size.values,
                decisionMakers: decisionMaker.values,
            },
        });
        return newArr;
    } else {
        return arr;
    }
};

var getDataAudienceProfile = R.compose(
    adjustProfile,
    R.path(['data', 'audienceProfile'])
);

var getBackupAudienceProfile = R.compose(
    adjustProfile,
    R.path(['_backupData', 'audienceProfile'])
);

var getDataNamedAudienceProfileFunc = R.curry((name, state) => {
    return R.compose(
        R.find(R.propEq('name', name)),
        getDataAudienceProfile
    )(state);
});

var getBackupNamedAudienceProfileFunc = R.curry((name, state) => {
    return R.compose(
        R.clone,
        R.find(R.propEq('name', name)),
        getBackupAudienceProfile
    )(state);
});

var getDataGender = getDataNamedAudienceProfileFunc('gender');
var getBackupGender = getBackupNamedAudienceProfileFunc('gender');

var getDataNewAndReturning = getDataNamedAudienceProfileFunc(
    'newAndReturningCustomers'
);
var getBackupNewAndReturning = getBackupNamedAudienceProfileFunc(
    'newAndReturningCustomers'
);

var getDataWebsites = getDataNamedAudienceProfileFunc('websiteVisitors');
var getBackupWebsites = getBackupNamedAudienceProfileFunc('websiteVisitors');

var getDataObjectives = getDataNamedAudienceProfileFunc('objectives');
var getBackupObjectives = getBackupNamedAudienceProfileFunc('objectives');

var getDataCustomers = getDataNamedAudienceProfileFunc('listOfCustomers');
var getBackupCustomers = getBackupNamedAudienceProfileFunc('listOfCustomers');

var getDataLocations = getDataNamedAudienceProfileFunc('location');
var getBackupLocations = getBackupNamedAudienceProfileFunc('location');

var getDataInterests = getDataNamedAudienceProfileFunc('audienceInterests');
var getBackupInterests = getBackupNamedAudienceProfileFunc('audienceInterests');

var getDataAge = getDataNamedAudienceProfileFunc('age');
var getBackupAge = getBackupNamedAudienceProfileFunc('age');

var getDataKeywords = getDataNamedAudienceProfileFunc('searchKeywords');
var getBackupKeywords = getBackupNamedAudienceProfileFunc('searchKeywords');

var getDataIncome = getDataNamedAudienceProfileFunc('income');
var getBackupIncome = getBackupNamedAudienceProfileFunc('income');

var getDataRelationshipStatus =
    getDataNamedAudienceProfileFunc('relationshipStatus');
var getBackupRelationshipStatus =
    getBackupNamedAudienceProfileFunc('relationshipStatus');

var getDataEducationLevel = getDataNamedAudienceProfileFunc('educationLevel');
var getBackupEducationLevel =
    getBackupNamedAudienceProfileFunc('educationLevel');

var getDataDeviceTarget = getDataNamedAudienceProfileFunc('deviceTarget');
var getBackupDeviceTarget = getBackupNamedAudienceProfileFunc('deviceTarget');

var getDataSizeAndDecision = getDataNamedAudienceProfileFunc('sizeAndDecision');
var getBackupSizeAndDecision =
    getBackupNamedAudienceProfileFunc('sizeAndDecision');

var getDataSector = getDataNamedAudienceProfileFunc('sector');
var getBackupSector = getBackupNamedAudienceProfileFunc('sector');

const getDataAudienceSchedule = getDataNamedAudienceProfileFunc('schedule');
const getBackupAudienceSchedule = getBackupNamedAudienceProfileFunc('schedule');

const getDataTargetType = R.compose(
    R.defaultTo({}),
    getDataNamedAudienceProfileFunc('targetType')
);
const getBackupTargetType = R.compose(
    R.defaultTo({}),
    getBackupNamedAudienceProfileFunc('targetType')
);

const getDataVideoAdFormat = getDataNamedAudienceProfileFunc('videoAdFormat');
const getBackupVideoAdFormat =
    getBackupNamedAudienceProfileFunc('videoAdformat');

const indicators = [
    {
        label: 'PROPOSAL.indicator_label_ad',
        navLabel: 'PROPOSAL.indicator_nav_label_ad',
        path: 'ads',
    },
    {
        label: 'PROPOSAL.indicator_label_ap',
        navLabel: 'PROPOSAL.indicator_nav_label_ap',
        path: 'audienceProfile',
    },
    {
        label: 'PROPOSAL.indicator_label_plan',
        navLabel: 'PROPOSAL.indicator_nav_label_plan',
        path: 'plan',
    },
];

const formIndicator = {
    label: 'PROPOSAL.indicator_label_form',
    navLabel: 'PROPOSAL.indicator_nav_label_form',
    path: 'form',
};

const getDefaultState = () => {
    return {
        data: {},
        _backupData: undefined,
        facebookProfile: {},
        _backupAds: undefined,
        isUploadingVideo: false,
        gaConversions: [],
        appUrlEnabled: false,
        suggestions: {},
        audienceVisibility: [],
    };
};

const baseType = R.compose(R.toUpper, _baseType);

// const dataBy = (name) =>
//     R.compose((fn) => fn(name), R.prop('profiles'), R.nthArg(1));

const uniqById = R.uniqBy(R.prop('id'));

const LIFE_TIME_BUDGET_WHITE_LIST = [24031, 24078, 25510, 27644];

export default {
    namespaced: true,
    state: getDefaultState(),
    mutations: {
        setData(state, data) {
            if (!isValueExist(data)) {
                return;
            }

            //just for check data mismatch
            data.channelAds.forEach((ad) => {
                if (!ad.executable) {
                    ad.executable = {};
                }

                if (ad.executable.value === false) {
                    ad.enabled = false;
                }
            });
            state.data = data;
            state._backupData = R.clone(data);
        },
        clearProposal(state) {
            Object.assign(state, getDefaultState());
        },
        updateData(state, data) {
            state.data = data;
        },
        setContinuously(state, value) {
            getDataSchedule(state).continuously = value;
        },
        setStartDate(state, date) {
            getDataSchedule(state).startDate = date;
            // const time = getTime(date);
            // let endDate = getDataSchedule(state).endDate;
            // endDate = moment(endDate).set(time).toDate();
            // getDataSchedule(state).endDate = endDate;
        },
        setEndDate(state, date) {
            // const time = getTime(getDataSchedule(state).startDate);
            // date = moment(date).set(time).toDate();
            getDataSchedule(state).endDate = date;
        },
        resetSchedule(state) {
            state.data.schedule = getBackupSchedule(state);
        },
        setTotalBudget(state, { value, type }) {
            getDataBudget(state)[type] = value || 0;
        },
        setChannelBudget(state, { name, value, key }) {
            var budgets = getDataBudget(state);
            var ret = R.compose(
                R.find(R.propEq('name', name)),
                R.propOr([], 'channels')
            )(budgets);

            if (ret) {
                Vue.set(ret, key, value);
                if (value == 0) {
                    ret.memory = 0;
                }
            }
        },
        resetBudget(state) {
            state.data.budget = getBackupBudget(state);
        },
        setFacebookProfile(state, profile) {
            state.facebookProfile = profile;
        },
        setSuggestions(state, suggesitons) {
            state.suggesitons = suggesitons;
        },
        updateGender(state, value) {
            var data = getDataGender(state).values;
            data[0].value = value[0];
            data[1].value = value[1];
        },
        resetGender(state) {
            getDataGender(state).values = R.compose(
                R.prop('values'),
                getBackupGender
            )(state);
        },
        updateNewAndReturningCustomers(state, value) {
            var values = getDataNewAndReturning(state).values;
            values.newPercentage = value;
            values.returningPercentage = 100 - value;
        },
        resetNewAndReturningCustomers(state) {
            getDataNewAndReturning(state).values = R.compose(
                R.prop('values'),
                getBackupNewAndReturning
            )(state);
        },
        setWebsiteVisitors(state, { type, value }) {
            var data = getDataWebsites(state);

            let valueGetter =
                type === GA ? R.path(['properties', 0]) : R.identity;

            data.values = [
                ...R.reject(R.propEq('type', type), data.values),
                {
                    ...valueGetter(value),
                    type,
                },
            ];
        },
        removeWebsiteVisitors(state, type) {
            var data = getDataWebsites(state);

            data.values = R.reject(R.propEq('type', type), data.values);
        },
        resetWebsiteVisitors(state) {
            getDataWebsites(state).values = R.compose(
                R.prop('values'),
                getBackupWebsites
            )(state);
        },
        setObjectives(state, values = []) {
            getDataObjectives(state).values = values;
        },
        removeObjectives(state, idx) {
            getDataObjectives(state).values.splice(idx, 1);
        },
        resetObjectives(state) {
            getDataObjectives(state).values = R.compose(
                R.prop('values'),
                getBackupObjectives
            )(state);
        },
        removeObjectiveByType(state, type) {
            const data = getDataObjectives(state);
            data.values = R.reject(R.propEq('analyticType', type), data.values);
        },
        addListOfCustomers(state, values = []) {
            getDataCustomers(state).values.push(...values);
        },
        removeListOfCustomers(state, idx) {
            getDataCustomers(state).values.splice(idx, 1);
        },
        resetListOfCustomers(state) {
            getDataCustomers(state).values = R.compose(
                R.prop('values'),
                getBackupCustomers
            )(state);
        },
        addLocations(state, values = []) {
            getDataLocations(state).values.push(...values);
        },
        setLocations(state, values = []) {
            getDataLocations(state).values = values;
        },
        removeLocation(state, idx) {
            getDataLocations(state).values.splice(idx, 1);
        },
        resetLocation(state) {
            getDataLocations(state).values = R.compose(
                R.prop('values'),
                getBackupLocations
            )(state);
        },
        addAudienceInterests(state, values = []) {
            const data = getDataInterests(state);
            let newValue = [...data.values, ...values];
            data.values = uniqById(newValue);
        },
        setAudienceInterests(state, values = []) {
            getDataInterests(state).values = uniqById(values);
        },
        removeAudienceInterests(state, idx) {
            getDataInterests(state).values.splice(idx, 1);
        },
        resetAudienceInterests(state) {
            getDataInterests(state).values = R.compose(
                R.prop('values'),
                getBackupInterests
            )(state);
        },
        updateAge(state, { idx, value }) {
            getDataAge(state).values[idx].value = value;
        },
        resetAge(state) {
            getDataAge(state).values = R.compose(
                R.prop('values'),
                getBackupAge
            )(state);
        },
        addSearchKeywords(state, values = []) {
            const data = getDataKeywords(state);
            const oldValues = data.values;

            data.values = R.uniq([...oldValues, ...values]);
        },
        setSearchKeywords(state, values = []) {
            getDataKeywords(state).values = values;
        },
        removeSearchKeywords(state, idx) {
            getDataKeywords(state).values.splice(idx, 1);
        },
        resetSearchKeywords(state) {
            getDataKeywords(state).values = R.compose(
                R.prop('values'),
                getBackupKeywords
            )(state);
        },
        updateIncome(state, { idx, value }) {
            getDataIncome(state).values[idx].value = value;
        },
        resetIncome(state) {
            getDataIncome(state).values = R.compose(
                R.prop('values'),
                getBackupIncome
            )(state);
        },
        updateRelationshipStatus(state, { idx, value }) {
            getDataRelationshipStatus(state).values[idx].value = value;
        },
        resetRelationshipStatus(state) {
            getDataRelationshipStatus(state).values = R.compose(
                R.prop('values'),
                getBackupRelationshipStatus
            )(state);
        },
        updateEducationLevel(state, { idx, value }) {
            getDataEducationLevel(state).values[idx].value = value;
        },
        resetEducationLevel(state) {
            getDataEducationLevel(state).values = R.compose(
                R.prop('values'),
                getBackupEducationLevel
            )(state);
        },
        updateDeviceTarget(state, { idx, value }) {
            getDataDeviceTarget(state).values[idx].value = value;
        },
        resetDeviceTarget(state) {
            getDataDeviceTarget(state).values = R.compose(
                R.prop('values'),
                getBackupDeviceTarget
            )(state);
        },
        updateSizeAndDecision(state, { type, index, value }) {
            var ret = getDataSizeAndDecision(state).values[type];
            ret.splice(index, 1, { name: ret[index].name, value });
        },
        resetSizeAndDecision(state) {
            var { sizes, decisionMakers } = R.compose(
                R.prop('values'),
                getBackupSizeAndDecision
            )(state);

            var profiles = state.data.audienceProfile;

            if (!R.isEmpty(sizes)) {
                let ret = R.find(
                    R.propEq('name', 'companySizeDecisionMaker'),
                    profiles
                );
                ret.values = sizes;
            }

            if (!R.isEmpty(decisionMakers)) {
                let ret = R.find(R.propEq('name', 'decisionMaker'), profiles);
                ret.values = decisionMakers;
            }
        },
        addSectors(state, values = []) {
            var { categories = [], customs = [] } = R.groupBy(
                R.ifElse(
                    R.has('value'),
                    R.always('categories'),
                    R.always('customs')
                ),
                values
            );
            var data = getDataSector(state).values;
            data.categories.push(...categories);
            data.customCategories.push(...customs);
        },
        removeSector(state, idx) {
            var { categories = [], customCategories = [] } =
                getDataSector(state).values;
            var cLength = categories.length;

            if (idx < cLength) {
                categories.splice(idx, 1);
            } else {
                customCategories.splice(idx - cLength, 1);
            }
        },
        resetSector(state) {
            getDataSector(state).values = R.compose(
                R.prop('values'),
                getBackupSector
            )(state);
        },
        backupAds(state) {
            state._backupAds = R.compose(R.clone, getChannelAds)(state);
        },
        restoreAds(state) {
            if (!state._backupAds) {
                return;
            }
            var dataChanged = R.compose(
                R.not,
                R.converge(R.equals, [R.prop('_backupAds'), getChannelAds])
            )(state);

            if (dataChanged) {
                state.data.channelAds = state._backupAds;
            }

            state._backupAds = undefined;
        },
        //TODO edit merge logic according to the data structure
        mergeChannelAds(state, newAds = []) {
            var ads = getChannelAds(state) || [];

            const _editing = R.path(['meta', 'isEditing']);

            const adsMap = R.indexBy(R.prop('creativeId'), newAds);

            ads = ads.map((ad) => {
                const { creativeId } = ad;
                let picks = ['executable', 'id'];
                const editMode = _editing(ad);
                const updated = adsMap[creativeId];

                if (!isValueExist(updated)) {
                    return ad;
                }

                if (!editMode) {
                    picks.push('creative');
                }

                if (updated.enabled || !editMode) {
                    picks.push('enabled');
                }

                return {
                    ...ad,
                    ...R.pick(picks, updated),
                };
            });

            state.data.channelAds = ads;
        },
        assignProposal(state, newData) {
            Object.assign(state.data, newData);
        },
        setIsUploadingVideo(state, value) {
            state.isUploadingVideo = value;
        },
        setAsap(state, value) {
            getDataSchedule(state).asap = value;
        },
        resetAds(state) {
            state.data.channelAds = R.clone(getBackupChannelAds(state));
        },
        resetAudienceProfile(state) {
            state.data.audienceProfile = R.clone(
                getBackupAudienceProfile(state)
            );
        },
        setGAConversionList(state, value = []) {
            state.gaConversions = value;
        },
        setAppUrlEnabled(state, value = false) {
            state.appUrlEnabled = value;
        },
        removeAd(state, creativeId) {
            state.data.channelAds = R.reject(
                R.propEq('creativeId', creativeId),
                state.data.channelAds
            );
        },
        setChannelAds(state, ads = []) {
            state.data.channelAds = adjustDuplicate(ads);
        },
        updateChannelAd(state, ad) {
            const ads = getChannelAds(state);
            const idx = ads.findIndex(R.propEq('creativeId', ad.creativeId));

            if (idx > -1) {
                state.data.channelAds = R.update(idx, ad, ads);
            }
        },
        // just update data to make watched function called
        updateMetaHash(state, hash) {
            const meta = state.data.meta || {};
            state.data.meta = {
                ...meta,
                hash: hash || generateId(),
            };
        },
        setAudienceVisibility(state, visibilities) {
            if (notEq(state.audienceVisibility, visibilities)) {
                state.audienceVisibility = visibilities;
            }
        },
        setAppstore(state, appstore) {
            state.data.meta = R.assocPath(
                ['webOrApp', 'appstore'],
                appstore,
                state.data.meta
            );
        },
        mergeGeneratedAds(state, ads) {
            const exists = R.pathOr(
                [],
                ['data', 'meta', 'generatedAds'],
                state
            );

            state.data.meta = R.assoc(
                'generatedAds',
                [...exists, ...filterNewAds(exists, ads)],
                state.data.meta
            );
        },
        updateAudienceSchedule(state, values) {
            Object.assign(getDataAudienceSchedule(state), {
                values,
            });
        },
        resetAudienceSchedule(state) {
            getDataAudienceSchedule(state).values = R.compose(
                R.prop('values'),
                getBackupAudienceSchedule
            )(state);
        },
        mergeAudienceProfile(state, data) {
            const nameEq = R.propEq('name');
            const find = R.find(R.__, data);
            R.compose(
                R.forEach((profile) =>
                    Object.assign(profile, find(nameEq(profile.name)) || {})
                ),
                R.reject(
                    R.propSatisfies(R.contains(R.__, ['schedule']), 'name')
                ),
                R.defaultTo([]),
                getDataAudienceProfile
            )(state);
        },
        updateTargetType(state, values) {
            Object.assign(getDataTargetType(state), {
                values,
            });
        },
        resetTargetType(state) {
            getDataTargetType(state).values = R.compose(
                R.prop('values'),
                getBackupTargetType
            )(state);
        },
        updateVideoAdFormat(state, values) {
            Object.assign(getDataVideoAdFormat(state), { values });
        },
        resetVideoAdFormat(state) {
            getDataVideoAdFormat(state).values = R.compose(
                R.prop('values'),
                getBackupVideoAdFormat
            )(state);
        },
        setAgeAdvanced(state, value) {
            Vue.set(getDataAge(state), 'advancedValues', value);
        },
        setRelationshipAdvanced(state, value) {
            Vue.set(getDataRelationshipStatus(state), 'advancedValues', value);
        },
        setLocationsAdvanced(state, value) {
            Vue.set(getDataLocations(state), 'advancedValues', value);
        },
        setObjectivesAdvanced(state, value) {
            Vue.set(getDataObjectives(state), 'advancedValues', value);
        },
        setListOfCustomers(state, value) {
            getDataCustomers(state).values = value;
        },
        updateKeywordsContent(state, { value, index }) {
            const data = getDataKeywords(state);
            const oldValues = data.values;

            data.values = R.uniq(R.update(index, value, oldValues));
        },
        setInterestsAdvanced(state, value) {
            Vue.set(getDataInterests(state), 'advancedValues', value);
        },
        updateIsBudgetLifeTime(state, value) {
            var budget = getDataBudget(state);
            budget.channels = budget.channels.map(
                R.assoc('isLifeTimeBudget', value)
            );
        },
        mergeBudget(state, budget) {
            const oldBudget = getDataBudget(state);

            const _nameIndex = R.indexBy(R.prop('name'));
            const oldChannels = _nameIndex(oldBudget.channels);
            let newChannels = _nameIndex(budget.channels);

            Object.entries(oldChannels).forEach(([key, value]) => {
                if (!key || key === 'undefined') {
                    return;
                }
                Object.assign(value, newChannels[key]);
                newChannels = R.dissoc(key, newChannels);
            });

            oldBudget.channels = [
                ...oldBudget.channels,
                ...Object.values(newChannels),
            ];

            state.data.budget = R.clone(oldBudget);
        },
        emptyKeywords(state) {
            const data = getDataKeywords(state);
            data.values = [];
        },
    },
    actions: {
        getFacebookProfile({ commit }, id) {
            return CampaignService.getFacebookProfile(id).then((data) => {
                commit('setFacebookProfile', data);
                return data;
            });
        },
        getSuggestions({ commit }, url) {
            return UtilsService.getSuggestions(url).then((data) => {
                commit('setSuggestions', data);
                return data;
            });
        },
        updateData({ commit }, data) {
            //special data which needs merge logic
            var propData = R.prop(R.__, data);
            commit('mergeChannelAds', propData('channelAds'));
            commit('mergeAudienceProfile', propData('audienceProfile'));

            var omitList = ['channelAds', 'audienceProfile', 'budget'];

            //if ads are being updated by user

            //other data that can merge as it is
            commit('assignProposal', R.omit(omitList, data));
        },
        resetPlan({ commit }) {
            commit('resetSchedule');
            commit('resetBudget');
        },
        checkAppstore({ commit }, proposal = {}) {
            const appstore = R.path(['meta', 'webOrApp', 'appstore'], proposal);
            const url = R.path(['meta', 'webOrApp', 'appStoreUrl'], proposal);
            if (isValidAppStoreUrl(url) && !isValueExist(appstore)) {
                UtilsService.searchApp({
                    term: url,
                    store: 'appstore',
                }).then((data = []) => {
                    if (R.propEq('length', 1, data)) {
                        commit('setAppstore', R.head(data));
                    }
                });
            }
        },
    },
    getters: {
        indicators(_, getters) {
            return getters.baseType === 'FORM'
                ? R.prepend(formIndicator, indicators)
                : indicators;
        },
        proposal: R.prop('data'),
        isFirst: R.path(['data', 'isFirst']),
        loaded: R.compose(R.not, R.isEmpty, R.prop('data')),
        continuously: R.compose(R.prop('continuously'), getDataSchedule),
        startDate: R.compose(toDate, R.prop('startDate'), getDataSchedule),
        endDate: R.compose(toDate, R.prop('endDate'), getDataSchedule),
        asap: R.compose(R.prop('asap'), getDataSchedule),
        dailyBudget: R.compose(R.prop('daily'), getDataBudget),
        totalBudget: (_, getters) => {
            let {
                startDate,
                endDate,
                asap,
                dailyBudget,
                isLifeTimeBudget,
                budgetChannels,
                continuously,
            } = getters;

            if (isLifeTimeBudget) {
                return R.compose(
                    R.sum,
                    R.filter(R.identity),
                    R.pluck('lifeTimeValue'),
                    R.filter(R.prop('visible'))
                )(budgetChannels);
            } else if (continuously) {
                return dailyBudget;
            } else {
                if (asap) {
                    startDate = new Date();
                }

                startDate = moment(startDate).startOf('day');
                endDate = moment(endDate).startOf('day');

                const diff = endDate.diff(startDate, 'days') + 1;

                return R.max(0, dailyBudget * diff);
            }
        },
        weeklyBudget: (_, getters) => getters.dailyBudget * 7,
        monthlyBudget: (_, getters) => getters.dailyBudget * 28,
        budgetChannels: R.compose(R.prop('channels'), getDataBudget),
        facebookProfile: R.prop('facebookProfile'),
        suggestions: R.prop('suggestions'),
        channels: getDataChannels,
        sites: getDataSites,
        searchEngines: getDataSearchEngines,
        urls: getDataUrls,
        contacts: getDataContacts,
        gender: getDataGender,
        genderBackup: getBackupGender,
        newAndReturningCustomers: getDataNewAndReturning,
        newAndReturningCustomersBackup: getBackupNewAndReturning,
        audienceProfile: getDataAudienceProfile,
        audienceProfileBackup: getBackupAudienceProfile,
        websiteVisitors: getDataWebsites,
        websiteVisitorsBackup: getBackupWebsites,
        objectives: getDataObjectives,
        objectivesBackup: getBackupObjectives,
        listOfCustomers: getDataCustomers,
        listOfCustomersBackup: getBackupCustomers,
        schedule: getDataSchedule,
        backupSchedule: getBackupSchedule,
        budget: getDataBudget,
        backupBudget: getBackupBudget,
        location: getDataLocations,
        locationBackup: getBackupLocations,
        audienceInterests: getDataInterests,
        audienceInterestsBackup: getBackupInterests,
        age: getDataAge,
        ageBackup: getBackupAge,
        searchKeywords: getDataKeywords,
        searchKeywordsBackup: getBackupKeywords,
        income: getDataIncome,
        incomeBackup: getBackupIncome,
        relationshipStatus: getDataRelationshipStatus,
        relationshipStatusBackup: getBackupRelationshipStatus,
        educationLevel: getDataEducationLevel,
        educationLevelBackup: getBackupEducationLevel,
        deviceTarget: getDataDeviceTarget,
        deviceTargetBackup: getBackupDeviceTarget,
        sizeAndDecision: getDataSizeAndDecision,
        sizeAndDecisionBackup: getBackupSizeAndDecision,
        sector: getDataSector,
        sectorBackup: getBackupSector,
        audienceSchedule: getDataAudienceSchedule,
        audienceScheduleBackup: getBackupAudienceSchedule,
        targetType: getDataTargetType,
        targetTypeBackup: getBackupTargetType,
        videoAdFormat: getDataVideoAdFormat,
        videoAdFormatBackup: getBackupTargetType,
        channelAds: getChannelAds,
        enabledChannelAds: R.compose(
            R.filter(R.propEq('enabled', true)),
            getChannelAds
        ),
        executableChannelAds: R.compose(
            R.filter(R.pathEq(['executable', 'value'], true)),
            getChannelAds
        ),
        channelAdsBackup: getBackupChannelAds,
        goals: R.pathOr([], ['data', 'objectives', 'goals']),
        hasPaymentMethod(_, _2, { user }) {
            return Boolean(R.path(['paymentMethods', 'length'], user));
        },
        canLaunch: (_, getters, { user }, rootGetters) => {
            let {
                totalBudget,
                enabledChannelAds: { length: enabledLength },
                changedOrNotRunning,
                isValidTargetTypeAdv,
                location = {},
                campaignObjective = {},
                includeYelp,
                objectives = {},
                fbPageNotPublished,
                fbFamilyExist,
                targetType = { values: [] },
                // budget,
                // audienceInterests = {},
            } = getters;

            totalBudget = totalBudget > 0;

            const isAdmin = rootGetters['user/isAdmin'];

            const hasPermission = rootGetters['permission/campaignLaunchable'];

            let reason = 'PROPOSAL.not_enough_information';

            let payment = !!(
                R.path(['paymentMethods', 'length'], user) ||
                isAdmin ||
                !rootGetters['campaign/needCreditCard']
            ); //in case of admin user skip payment check

            const ads = !!enabledLength;

            let value =
                ads &&
                totalBudget &&
                payment &&
                changedOrNotRunning &&
                hasPermission;

            if (!hasPermission) {
                reason = 'COMMON.permission_not_enough';
            } else if (!payment) {
                reason = 'PROPOSAL.launch_check_payment';
            } else if (!changedOrNotRunning) {
                reason = '';
            }

            if (getters.baseType === 'FORM') {
                const formHasNobody = rootGetters['form/formHasNoBody'],
                    formHasBlank = rootGetters['form/formHasBlank'];
                if (formHasNobody) {
                    reason = 'FORM.form_has_no_body';
                    value = value && !formHasNobody;
                } else if (formHasBlank) {
                    reason = 'FORM.form_has_blank';
                    value = value && !formHasBlank;
                }
            }

            if (!includeYelp && R.pathEq(['values', 'length'], 0, location)) {
                value = false;
                reason = 'PROPOSAL.launch_location_empty';
            } else if (!isValidTargetTypeAdv) {
                value = false;
                reason = 'REMARKETING.invalid_remarketing';
            } else if (
                campaignObjective === OBJECTIVES.CONVERSIONS &&
                !isValueExist(objectives.values)
                // !isValueExist(rootGetters['campaign/pixelConversions'])
            ) {
                value = false;
                reason = 'PROPOSAL.launch_conversion_not_added';
            }

            if (fbFamilyExist && fbPageNotPublished) {
                value = false;
                reason = 'PROPOSAL.launch_fb_page_not_published';
            }

            const { value: targetInterestValue } =
                R.find(R.propEq('name', 'interest'), targetType.values || []) ||
                {};

            if (
                isValueExist(targetInterestValue) &&
                R.none(R.propEq('value', true), targetType.values || [])
            ) {
                value = false;
                reason = 'PROPOSAL.launch_no_target';
            }

            // const { value: targetInterestValue } =
            //     R.find(R.propEq('name', 'interest'), targetType.values || []) ||
            //     {};

            // if (
            //     targetInterestValue === true &&
            //     !isValueExist(audienceInterests.values)
            // ) {
            //     value = false;
            //     reason = 'PROPOSAL.launch_no_target';
            // }

            // const hasValueUnderMinBudget = R.compose(
            //     R.any(({ value, min, isLifeTimeBudget, lifeTimeBudget }) =>
            //         isLifeTimeBudget ? lifeTimeBudget < min : value < min
            //     ),
            //     R.filter(R.prop('visible')),
            //     R.propOr([], 'channels')
            // )(budget);

            // if (hasValueUnderMinBudget) {
            //     value = false;
            //     reason = 'PROPOSAL.below_minimum_budget';
            // }

            // const connections =
            //     rootGetters['campaign/intelConnectionStatusForCheckingLaunch'] || {};

            // for (let [key, status] of Object.entries(connections)) {
            //     if (!status) {
            //         value = false;
            //         reason = `PROPOSAL.connection_lost_re_connect_${key}`;
            //         break;
            //     }
            // }

            return {
                value,
                reason,
            };
        },
        summary: R.pathOr([], ['data', 'audienceSummary']),
        isUploadingVideo: R.prop('isUploadingVideo'),
        isPlanResetable(state) {
            return (
                //date string date object
                !R.equals(
                    JSON.stringify(getDataSchedule(state)),
                    JSON.stringify(getBackupSchedule(state))
                ) || !R.equals(getDataBudget(state), getBackupBudget(state))
            );
        },
        audienceTags: R.pathOr([], ['data', 'audienceTags']),
        backupData: R.prop('_backupData'),
        fbPixel(state, getters) {
            const websiteVisitors = getters.websiteVisitors;
            if (!websiteVisitors || !websiteVisitors.values) return null;
            const fbPixel = websiteVisitors.values.find(
                R.propEq('type', PIXEL)
            );
            return fbPixel;
        },
        fbApp(state, getters) {
            const websiteVisitors = getters.websiteVisitors;
            if (!websiteVisitors || !websiteVisitors.values) return null;

            return websiteVisitors.values.find(R.propEq('type', FB_APP));
        },
        ga(state, getters) {
            const websiteVisitors = getters.websiteVisitors;
            if (!websiteVisitors || !websiteVisitors.values) return null;
            return websiteVisitors.values.find(R.propEq('type', GA));
        },

        pixelConversions(state, getters) {
            const websiteVisitors = getters.websiteVisitors;
            if (!websiteVisitors || !websiteVisitors.values) return [];
            const fbPixel = websiteVisitors.values.find(
                R.propEq('type', PIXEL)
            );
            if (!fbPixel || !fbPixel.availableConversions) return [];
            return fbPixel.availableConversions;
        },
        appUrlEnabled: R.prop('appUrlEnabled'),
        changedOrNotRunning(
            { _backupData, data },
            getters,
            { user },
            rootGetters
        ) {
            if (!R.equals(_backupData, data)) {
                return true;
            }
            return !R.propEq(
                'status',
                'running',
                rootGetters['campaign/campaign'] || {}
            );
        },
        baseType(state, { campaignObjective: objective }) {
            return baseType({
                objective,
                webOrApp: R.path(['data', 'meta', 'webOrApp'], state),
            });
        },
        baseUrl: R.pathOr('', ['data', 'meta', 'webOrApp', 'websiteUrl']),
        developerSiteUrl: R.pathOr('', [
            'data',
            'meta',
            'webOrApp',
            'developerSiteUrl',
        ]),
        baseAppStoreUrl: R.pathOr('', [
            'data',
            'meta',
            'webOrApp',
            'appStoreUrl',
        ]),
        baseAppstore: R.pathOr('', ['data', 'meta', 'webOrApp', 'appstore']),
        basePlayStoreUrl: R.pathOr('', [
            'data',
            'meta',
            'webOrApp',
            'playStoreUrl',
        ]),
        generatedAds: R.pathOr([], ['data', 'meta', 'generatedAds']),
        isAppType(_, { baseType = '' }) {
            return baseType.toUpperCase() === 'APP';
        },
        appleAdAvailabe(_, { isAppType, baseAppstore }) {
            return isAppType && isValueExist(baseAppstore);
        },
        audienceVisibility: R.prop('audienceVisibility'),
        visibleAudiences: R.compose(
            R.without(['listOfCustomers']),
            R.pluck('name'),
            R.filter(R.prop('visible')),
            R.prop('audienceVisibility')
        ),
        meta: R.pathOr({}, ['data', 'meta']),
        adsByChannel:
            (_, { channelAds = [] }) =>
            (channel) => {
                return R.compose(
                    R.defaultTo([]),
                    R.prop(channel),
                    R.groupBy(R.prop('channel'))
                )(channelAds);
            },
        fbFamilyExist: R.compose(
            R.any(isFbFamily),
            R.propOr([], 'enabledChannelAds'),
            R.nthArg(1)
        ),
        googleFamilyExist: R.compose(
            R.any(isGoogleType),
            R.propOr([], 'enabledChannelAds'),
            R.nthArg(1)
        ),
        hasYoutubeChannel: R.compose(
            R.any(isYouTube),
            R.propOr([], 'enabledChannelAds'),
            R.nthArg(1)
        ),
        hasFbKindChannelFromAll: R.compose(
            R.any(R.propSatisfies(R.test(/facebook|instagram/im), 'channel')),
            R.propOr([], 'channelAds'),
            R.nthArg(1)
        ),
        visibleBudgets: R.compose(
            R.filter(R.propEq('visible', true)),
            R.prop('budgetChannels'),
            R.nthArg(1)
        ),
        isValidTargetTypeAdv(_, { targetType = {} }) {
            return R.compose(
                R.not,
                R.find(({ name, value, data }) => {
                    const origin =
                        name === 'remarketing'
                            ? data
                            : R.propOr({}, 'origin', data);
                    return (
                        value &&
                        isValueExist(origin) &&
                        checkRemarketingAdvSet(origin) &&
                        R.path(['meta', 'dirty'], data)
                    );
                }),
                R.propOr([], 'values')
            )(targetType);
        },
        campaignObjectives: R.pathOr([], ['data', 'meta', 'objectives']),
        campaignObjective: R.compose(
            R.path(['campaignObjectives', 0]),
            R.nthArg(1)
        ),
        profiles:
            (state) =>
            (name = '') => {
                return R.compose(
                    R.prop(name),
                    R.indexBy(R.prop('name')),
                    R.pathOr([], ['data', 'audienceProfile'])
                )(state);
            },
        adsMap: R.compose(
            R.indexBy(R.prop('creativeId')),
            R.prop('channelAds'),
            R.nthArg(1)
        ),
        includeYelp: R.compose(
            isValueExist,
            R.find(isYelpSearch),
            R.prop('channelAds'),
            R.nthArg(1)
        ),
        forbiddenUrlsForCreative(_, { campaignObjective, baseType }) {
            const _forbiddens = [
                'instagram.com/p/',
                'facebook.com',
                'play.google.com',
                'apps.apple.com',
                'url.kr',
                // 'open.kakao.com',
            ];

            switch (campaignObjective) {
                case OBJECTIVES.LINK_CLICKS:
                case OBJECTIVES.REACH:
                    return _forbiddens;
                case OBJECTIVES.CONVERSIONS:
                    return baseType === 'WEB' ? _forbiddens : [];
            }

            return [];
        },
        includeApppleSearch: R.compose(
            isValueExist,
            R.find(isAppleSearch),
            R.prop('channelAds'),
            R.nthArg(1)
        ),
        budgetLifeTimePossible(state, getters, _, rootGetters) {
            const { campaignObjective, continuously, isLifeTimeBudget } =
                getters;

            const isWadizAccount = LIFE_TIME_BUDGET_WHITE_LIST.find(
                (id) => id == rootGetters['user/accountId']
            );
            const fbExists = rootGetters['proposal/fbFamilyExist'];

            return (
                isLifeTimeBudget ||
                (isValueExist(campaignObjective) &&
                    !continuously &&
                    isWadizAccount &&
                    fbExists)
            );
        },
        isLifeTimeBudget(_, { budgetChannels = [] }) {
            return R.any(R.propEq('isLifeTimeBudget', true), budgetChannels);
        },
        budgetTypeChangable(_, { onlyLifeBudget }, _2, rootGetters) {
            return !rootGetters['campaign/executed_at'] && !onlyLifeBudget;
        },
        isLifeTimeBudgetReduced(_, getters) {
            const {
                isLifeTimeBudget,
                budgetTypeChangable,
                backupBudget,
                budget,
            } = getters;

            if (
                !isLifeTimeBudget ||
                budgetTypeChangable ||
                !backupBudget.lifeTime
            ) {
                return false;
            }

            if (backupBudget.lifeTime > budget.lifeTime) {
                return true;
            }
        },
        isLeadForm(_, getters) {
            return getters.baseType === 'FORM';
        },
        fbPageNotPublished({ facebookProfile: p = {} }) {
            return isValueExist(p) && p.is_published === false;
        },
        budgetKey(_, { isLifeTimeBudget }) {
            return isLifeTimeBudget ? 'lifeTimeValue' : 'value';
        },
        onlyLifeBudget(_, getters, _2, rootGetters) {
            return (
                rootGetters['user/isWadizStore'] &&
                getters['hasFbKindChannelFromAll']
            );
        },
    },
};
